import { memo, useMemo } from 'react'
import tw from 'twin.macro'
import { Chip } from 'src/components/shared/Chip'
import { colors } from 'src/theme'
import { ExpirationIndicator } from './types'

interface Props {
  indicator: ExpirationIndicator
  /** Ignored when `label` is given — the filter options label the bucket, not a row. */
  daysLeft?: number
  /** Overrides the derived text, matching upstream's `ExpirationIndicatorChip`. */
  label?: string
}

/**
 * `design-rules/02-molecules.md` — Status / Type / Expiration chips.
 * Colour comes from the `ExpirationIndicator` enum, never from day-count math here.
 * 200-bg / 500-text pairs, taken from `00-tokens.md`.
 */
const indicatorMap = {
  [ExpirationIndicator.Expired]: {
    color: colors.COLORS_RED_500,
    bgStyle: tw`bg-[#FFDCDC]`,
  },
  [ExpirationIndicator.ExpiringSoon]: {
    color: colors.COLORS_ORANGE_500,
    bgStyle: tw`bg-[#FFEED6]`,
  },
  [ExpirationIndicator.Valid]: {
    color: colors.COLORS_GREEN_500,
    bgStyle: tw`bg-[#E6F9E9]`,
  },
}

export const getExpirationChipLabel = (
  indicator: ExpirationIndicator,
  daysLeft: number,
): string => {
  if (daysLeft === 0) {
    return 'Today'
  }

  if (indicator === ExpirationIndicator.Expired) {
    return 'Expired'
  }

  return daysLeft === 1 ? '1 day' : `${daysLeft} days`
}

const ExpirationIndicatorChipComponent = ({ indicator, daysLeft = 0, label }: Props) => {
  const params = useMemo(
    () => indicatorMap[indicator] || indicatorMap[ExpirationIndicator.Valid],
    [indicator],
  )

  return (
    <div css={tw`flex items-center`}>
      <div css={tw`py-1 rounded-[10px]`}>
        <Chip
          className={params.bgStyle}
          textColor={params.color}
          text={label ?? getExpirationChipLabel(indicator, daysLeft)}
        />
      </div>
    </div>
  )
}

export const ExpirationIndicatorChip = memo(ExpirationIndicatorChipComponent)
